import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import { CurrencyLogo } from 'components/Logo'
import { TagComponent, tagTypes } from 'components/Tags'
import { TokenPairImage } from 'components/TokenImage'
import { BIG_INT_ZERO } from 'config/constants'
import { useTranslation } from 'contexts/Localization'
import useTotalSupply from 'hooks/useTotalSupply'
import React from 'react'
import { Link } from 'react-router-dom'
import { useRemovalOrders } from 'state/burn/hooks'
import { useTokenBalance } from 'state/wallet/hooks'
import { currencyId } from 'utils/currencyId'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { JSBI, Percent } from 'vvs-sdk'
import { AddIcon, Box, Button, CardBody, Flex, Text } from 'vvs-uikit'
import { FixedHeightRow, StyledCard } from '../styles'
import { LiquidityCardProps } from '../types'

const SuppliedCard: React.FC<LiquidityCardProps> = (props) => {
  const { t } = useTranslation()

  const { account, pair, onBeforeNavigate, farms } = props

  const existingFarm = farms.find((f) => {
    return (
      !f.isFinished &&
      ((f.quoteToken.address === pair.token1.address && f.token.address === pair.token0.address) ||
        (f.quoteToken.address === pair.token0.address && f.token.address === pair.token1.address))
    )
  })

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)

  const orders = useRemovalOrders()
  const existsRemovalOrder = orders.find(
    (o) =>
      o.userData.stakedBalance.isGreaterThan('0') &&
      o.lpAddress.toLowerCase() === pair?.liquidityToken.address.toLowerCase(),
  )

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return (
    <StyledCard>
      <CardBody p={0}>
        <AutoColumn gap="8px" style={{ padding: '16px' }}>
          <Flex marginBottom="15px" justifyContent="space-between">
            <TagComponent title={t('Supplied')} variant={tagTypes.SUCCESS} />
          </Flex>

          <Flex justifyContent="space-between" alignItems="center">
            <TokenPairImage
              variant="inverted"
              primaryToken={pair.token0}
              secondaryToken={pair.token1}
              width={32}
              height={32}
            />
            <Text fontSize="20px" bold>
              {`${currency0.symbol}/${currency1.symbol}`}
            </Text>
          </Flex>

          <Flex justifyContent="flex-start" role="button" my={3}>
            <Text fontSize="20px" bold>
              {userPoolBalance?.toSignificant(4)}
            </Text>
          </Flex>

          <FixedHeightRow>
            <RowFixed>
              <CurrencyLogo size="20px" currency={currency0} />
              <Text color="textSubtle" ml="4px">
                {t('Pooled %asset%', {
                  asset: currency0.symbol,
                })}
              </Text>
            </RowFixed>
            {token0Deposited ? (
              <RowFixed>
                <Text ml="6px">{token0Deposited?.toSignificant(6)}</Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <RowFixed>
              <CurrencyLogo size="20px" currency={currency1} />
              <Text color="textSubtle" ml="4px">
                {t('Pooled %asset%', {
                  asset: currency1.symbol,
                })}
              </Text>
            </RowFixed>
            {token1Deposited ? (
              <RowFixed>
                <Text ml="6px">{token1Deposited?.toSignificant(6)}</Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <Text color="textSubtle">{t('Share of Pool')}</Text>
            <Text>
              {poolTokenPercentage
                ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
                : '-'}
            </Text>
          </FixedHeightRow>

          {userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, BIG_INT_ZERO) && (
            <Box mt={3}>
              <Flex flexDirection="row" justifyContent="space-between">
                {!existingFarm ? (
                  <Button
                    as={Link}
                    to={`/remove/instant/${currencyId(currency0)}/${currencyId(currency1)}`}
                    variant="primary"
                    width="100%"
                    mb="8px"
                    scale="lg"
                  >
                    {t('Remove')}
                  </Button>
                ) : (
                  <>
                    <Button
                      as={Link}
                      to={`/remove/instant/${currencyId(currency0)}/${currencyId(currency1)}`}
                      variant="primary"
                      width="48%"
                      mb="8px"
                      scale="lg"
                    >
                      {t('Remove')}
                    </Button>
                    <Button
                      as={Link}
                      to={`/remove/smart/${currencyId(currency0)}/${currencyId(currency1)}`}
                      onClick={(e) => onBeforeNavigate(e, pair, existsRemovalOrder)}
                      variant="primary"
                      width="48%"
                      scale="lg"
                    >
                      {t('Smart Remove')}
                    </Button>
                  </>
                )}
              </Flex>
              <Flex>
                <Button
                  as={Link}
                  to={`/add/${currencyId(currency0)}/${currencyId(currency1)}`}
                  variant="text"
                  startIcon={<AddIcon color="primary" />}
                  width="100%"
                >
                  {t('Supply More')}
                </Button>
              </Flex>
            </Box>
          )}
        </AutoColumn>
      </CardBody>
    </StyledCard>
  )
}

export default SuppliedCard
